<template>
  <div
    class="flex flex-col bg-[--dropdown-bg] rounded-lg p-3 gap-3"
    style="
      width: 400px;
      max-height: 600px;
      box-shadow: var(--dropdown-boxshadow);
    "
    @click.stop
  >
    <div class="flex items-center gap-2">
      <div
        class="flex items-center justify-center p-1 rounded-lg cursor-pointer hover:bg-[--white-opacity-8]"
        @click="$emit('close')"
      >
        <ThemeImage
          :icon="imageNames.BACK_ARROW_WHITE"
          alt="back_arrow"
          class="size-4"
        />
      </div>
      <span class="text-sm font-semibold text_color">
        Add members to {{ appName }}
      </span>
    </div>

    <div class="flex items-center gap-2">
      <div class="flex-1">
        <SearchableMultiSelectDropdown
          v-model="selectedItems"
          :tabs="tabs"
          :options="optionsArray"
          :tab-selection-modes="tabSelectionModes"
          placeholder="Enter members to add"
          :relative-to-viewport="false"
          @selection-change="handleSelectionChange"
          @tab-change="handleTabChange"
          @dropdown-open="$emit('dropdown-open')"
          @dropdown-close="$emit('dropdown-close')"
          :show-select-all="true"
          :select-all-users-by-default="true"
        />
      </div>
      <a-button
        class="primary_medium"
        :disabled="addingMembers || !hasNewSelections"
        @click="handleAddMembers"
      >
        {{ addingMembers ? "Adding..." : "Add" }}
      </a-button>
    </div>

    <div v-if="loadingUsers" class="flex justify-center items-center py-8">
      <CustomLoader :size="24" />
    </div>

    <template v-else>
      <div v-if="installedUsers.length > 0" class="flex flex-col gap-2">
        <div class="flex flex-col gap-1 max-h-[300px] overflow-y-auto">
          <div
            v-for="user in installedUsers"
            :key="user.id"
            class="flex items-center justify-between p-2 rounded-lg hover:bg-[--white-opacity-4] group"
          >
            <div class="flex items-center gap-2 flex-1 min-w-0">
              <ProfileIcon :data="user" :size="32" class="flex-shrink-0" />
              <div class="flex flex-col min-w-0 flex-1">
                <span class="text-sm text_color truncate">
                  {{ user.name }}
                </span>
                <span class="text-xs secondary_color truncate">
                  {{ user.email }}
                </span>
              </div>
            </div>

            <a-dropdown
              v-if="user.id !== currentUserId"
              placement="bottomRight"
              :trigger="['click']"
              @click.stop
            >
              <div
                class="flex items-center justify-center p-1 rounded-lg cursor-pointer hover:bg-[--white-opacity-8] opacity-0 group-hover:opacity-100 transition-opacity"
                @click.stop
              >
                <ThemeImage
                  :icon="imageNames.MORE_VERTICAL"
                  alt="more_options"
                  class="size-4"
                />
              </div>
              <template #overlay>
                <a-menu>
                  <a-menu-item key="remove" @click="handleRemoveMember(user)">
                    <div class="flex items-center gap-2">
                      <ThemeImage
                        :icon="imageNames.ENCLOSED_CROSS_GREY"
                        alt="remove"
                        class="size-4"
                      />
                      <span class="text-sm">Remove</span>
                    </div>
                  </a-menu-item>
                </a-menu>
              </template>
            </a-dropdown>
          </div>
        </div>
      </div>

      <div v-else class="flex justify-center items-center py-8">
        <span class="secondary_color text-sm">No members installed</span>
      </div>
    </template>

    <ConfirmationModal
      v-if="showRemoveModal"
      :visible="showRemoveModal"
      :title="`Remove ${userToRemove?.name}?`"
      :message="`Are you sure you want to remove this user from ${appName}?`"
      width="400px"
      position="top"
      type="delete"
      confirmText="Remove"
      cancelText="Cancel"
      loadingText="Removing..."
      :loading="removing"
      @confirm="confirmRemove"
      @cancel="closeRemoveModal"
      @update:visible="(val) => !val && closeRemoveModal()"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { message } from "ant-design-vue";
import SearchableMultiSelectDropdown from "@/src/components/SearchableMultiSelectDropdown.vue";
import ProfileIcon from "@/src/components/ProfileIcon.vue";
import CustomLoader from "@/src/components/CustomLoader.vue";
import ConfirmationModal from "@/src/components/ConfirmationModal.vue";
import ThemeImage from "@/src/components/ThemeImage.vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import eventBus from "@/src/utils/eventBus.js";
import AppStoreService from "@/src/api/AppStoreService.js";
import DashboardService from "@/src/api/DashboardService.js";
import store from "@/src/store/index.js";

const billingVersion = computed(() => store.getters["billing/billingVersion"]); // v0, v1, v3, v4

const props = defineProps({
  appId: {
    type: Number,
    required: true,
  },
  appName: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(["close", "members-updated"]);

const currentUserId = Number(localStorage.getItem("userId"));
const installedUserIds = ref([]);
const installedUsers = ref([]);
const loadingUsers = ref(false);
const addingMembers = ref(false);
const removing = ref(false);
const showRemoveModal = ref(false);
const userToRemove = ref(null);
const appPlan = ref(null);

const users = ref([]);
const teams = ref([]);

const selectedItems = ref([]);
const currentActiveTab = ref({ name: "Users", index: 0 });

const tabs = ["Users", "Teams"];
const tabSelectionModes = ["multi", "multi"];

const optionsArray = computed(() => {
  const installedSet = new Set(installedUserIds.value);
  const currentUser = Number(localStorage.getItem("userId"));
  const userOptions = users.value
    .filter((user) => user.id !== currentUser)
    .filter((user) => !installedSet.has(user.id))
    .map((user) => ({
      label: user.name,
      value: user.id,
      userData: user,
    }));

  const teamOptions = teams.value.map((team) => ({
    label: team.name,
    value: team.id,
  }));

  return [userOptions, teamOptions];
});

const selectedUsersInCurrentTab = computed(() => {
  const userOptionIds = new Set(
    (optionsArray.value[0] || []).map((o) => o.value),
  );
  return (selectedItems.value || []).filter((id) => userOptionIds.has(id));
});

const selectedTeamsInCurrentTab = computed(() => {
  const teamOptionIds = new Set(
    (optionsArray.value[1] || []).map((o) => o.value),
  );
  return (selectedItems.value || []).filter((id) => teamOptionIds.has(id));
});

const hasNewSelections = computed(() => {
  if (currentActiveTab.value.name === "Users") {
    return selectedUsersInCurrentTab.value.length > 0;
  } else if (currentActiveTab.value.name === "Teams") {
    return selectedTeamsInCurrentTab.value.length > 0;
  }
  return false;
});

const handleSelectionChange = ({ selectedValues: newValues }) => {
  selectedItems.value = newValues || [];
};

const handleTabChange = (tabData) => {
  currentActiveTab.value = {
    name: tabData.tabName,
    index: tabData.tabIndex,
  };
};

const fetchUsers = async () => {
  try {
    const response = await DashboardService.fetchUsers("active");
    if (response?.success) {
      const currentUserId = Number(localStorage.getItem("userId"));
      users.value = (response.users || [])
        .filter((user) => user.id !== currentUserId)
        .map((user) => ({
          id: user.id,
          name:
            user.name ||
            `${user.first_name || ""} ${user.last_name || ""}`.trim(),
          email: user.email,
          profile_pic: user.profile_pic,
        }));
    }
  } catch (error) {
    console.error("Error fetching users:", error);
  }
};

const fetchTeams = async () => {
  try {
    const response = await DashboardService.fetchTeams();
    if (response?.success) {
      teams.value = (response.teams || []).map((team) => ({
        id: team.id,
        name: team.name,
      }));
    }
  } catch (error) {
    console.error("Error fetching teams:", error);
  }
};

const fetchInstalledUsers = async () => {
  if (!props.appId) return;

  try {
    loadingUsers.value = true;
    const resp = await AppStoreService.getAppById(props.appId);
    
    if (!resp?.success || !resp.app) {
      installedUserIds.value = [];
      installedUsers.value = [];
      selectedItems.value = [];
      currentActiveTab.value = { name: "Users", index: 0 };
      appPlan.value = null;
      return;
    }

    const app = resp.app;
    const allUserIds = new Set();
    const userMap = new Map();
    if (app.plan) {
      appPlan.value = app.plan;
    }

    const usersList = Array.isArray(app.installed_users)
      ? app.installed_users
      : [];
    usersList.forEach((u) => {
      allUserIds.add(u.id);
      if (!userMap.has(u.id)) userMap.set(u.id, u);
    });

    const idsList = Array.isArray(app.installed_user_ids)
      ? app.installed_user_ids
      : [];
    idsList.forEach((id) => allUserIds.add(id));

    const missingIds = Array.from(allUserIds).filter((id) => !userMap.has(id));
    if (missingIds.length > 0) {
      try {
        const usersResponse = await AppStoreService.getUsersByIds(missingIds);
        if (usersResponse?.success && usersResponse?.users) {
          usersResponse.users.forEach((u) => userMap.set(u.id, u));
        }
      } catch (e) {
        console.error("Error resolving user ids:", e);
      }
    }

    installedUserIds.value = Array.from(allUserIds);
    installedUsers.value = installedUserIds.value
      .map((id) => userMap.get(id))
      .filter(Boolean);

    selectedItems.value = [];
    currentActiveTab.value = { name: "Users", index: 0 };
  } catch (error) {
    console.error("Error fetching installed users:", error);
    installedUserIds.value = [];
    installedUsers.value = [];
  } finally {
    loadingUsers.value = false;
  }
};

const handleAddMembers = async () => {
  try {
    addingMembers.value = true;

    let userIds = [];
    let teamIds = [];

    if (currentActiveTab.value.name === "Users") {
      userIds = selectedUsersInCurrentTab.value;
    } else if (currentActiveTab.value.name === "Teams") {
      teamIds = selectedTeamsInCurrentTab.value;
    }

    const response = await AppStoreService.bulkInstallApp(
      [props.appId],
      userIds,
      teamIds,
      appPlan.value ? appPlan.value.id : null,
      billingVersion.value
    );

    if (response?.success) {
      message.success("Members added successfully");
      selectedItems.value = [];
      await fetchInstalledUsers();
      emit("members-updated");
      eventBus.emit("app-installed");
    } else {
      message.error(response?.error || "Failed to add members");
    }
  } catch (error) {
    console.error("Error adding members:", error);
    message.error("An error occurred while adding members");
  } finally {
    addingMembers.value = false;
  }
};

const handleRemoveMember = (user) => {
  userToRemove.value = user;
  showRemoveModal.value = true;
};

const closeRemoveModal = () => {
  showRemoveModal.value = false;
  userToRemove.value = null;
};

const confirmRemove = async () => {
  if (!userToRemove.value) return;

  try {
    if (userToRemove.value.id === currentUserId) {
      message.warning("You cannot remove yourself.");
      return closeRemoveModal();
    }
    removing.value = true;
    const response = await AppStoreService.bulkUninstallApps([props.appId], [
      userToRemove.value.id,
    ], billingVersion.value);

    if (response?.success) {
      message.success("Member removed successfully!");
      closeRemoveModal();
      await fetchInstalledUsers();
      emit("members-updated");
    } else {
      message.error(response?.error || "Failed to remove member");
    }
  } catch (error) {
    console.error("Error removing member:", error);
    message.error("An error occurred while removing the member");
  } finally {
    removing.value = false;
  }
};

onMounted(async () => {
  if (props.appId) {
    await Promise.all([fetchUsers(), fetchTeams(), fetchInstalledUsers()]);
  }
});

watch(
  () => props.appId,
  async () => {
    if (props.appId) {
      await Promise.all([fetchUsers(), fetchTeams(), fetchInstalledUsers()]);
    }
  },
);
</script>

<style scoped>
.add_members_dropdown {
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.15);
}

:deep(.ant-dropdown-menu) {
  padding: 4px;
}

:deep(.ant-dropdown-menu-item) {
  padding: 8px 12px;
  border-radius: 6px;
}
</style>
